import { createFileRoute, useLocation } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { match } from 'ts-pattern';
import { HStack, VStack } from '@/components/Layout';
import { DeviceType } from '@/constants/device';
import Processing from '@/features/import-csv/-components/Processing';
import PreviewFile from '@/features/import-csv/-features/PreviewFile';
import { ImportCsvSearchSchema } from '@/features/import-csv/zod-schemas';
import { defaultNS, useTranslation } from '@/lib/i18n';
import useGetExternalItSaasAppIntegrations from '../../-queries/useGetExternalItSaasAppIntegrations';
import AddStepperNavigation from './-component/AddStepperNavigation';
import AddStepperStepTitleCard from './-component/AddStepperStepTitleCard';
import { EXTERNAL_IT_SAAS_PREVIEW_HASH, type EXTERNAL_IT_SAAS_PREVIEW_HASH_VALUE } from './-constants/hash';
import useTableRows from './-mutations/useTableRows';
import useGetExternalItSaasAppIntegrationSyncPreview from './-queries/useGetExternalItSaasAppIntegrationSyncPreview';
import useGetExternalItSaasAppIntegrationSyncStatus from './-queries/useGetExternalItSaasAppIntegrationSyncStatus';
export const Route = createFileRoute('/_staff/devices/external-it-saas/$appIntegrationId/preview/')({
  component: RouteComponent,
  validateSearch: zodValidator(ImportCsvSearchSchema)
});
function RouteComponent() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const location = useLocation();
  const {
    appIntegrationId
  } = Route.useParams();
  const {
    data: appIntegrations
  } = useGetExternalItSaasAppIntegrations();
  const appIntegration = appIntegrations.groups.flatMap(group => [group, ...group.items]).find(({
    app
  }) => app.appIntegrationId === appIntegrationId)?.app;
  const isLanscope = appIntegration?.appId === 'LANSCOPE_AN';
  const hash = location.hash ? location.hash.replace('#', '') as EXTERNAL_IT_SAAS_PREVIEW_HASH_VALUE : EXTERNAL_IT_SAAS_PREVIEW_HASH.COMPUTER;
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.devices.importCsv.deviceType'
  });
  const {
    t: tPreview
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.devices.externalItSaas.preview'
  });
  const deviceType = match(hash).with(EXTERNAL_IT_SAAS_PREVIEW_HASH.COMPUTER, () => DeviceType.COMPUTER).with(EXTERNAL_IT_SAAS_PREVIEW_HASH.MOBILE, () => DeviceType.MOBILE).with(EXTERNAL_IT_SAAS_PREVIEW_HASH.MONITOR, () => DeviceType.MONITOR).with(EXTERNAL_IT_SAAS_PREVIEW_HASH.SERVER, () => DeviceType.SERVER).with(EXTERNAL_IT_SAAS_PREVIEW_HASH.PRINTER, () => DeviceType.PRINTER).with(EXTERNAL_IT_SAAS_PREVIEW_HASH.OTHER, () => DeviceType.OTHER).exhaustive();
  const unit = match(deviceType).with(DeviceType.COMPUTER, () => t('unit.computers')).with(DeviceType.MOBILE, () => t('unit.mobiles')).with(DeviceType.MONITOR, () => t('unit.monitors')).with(DeviceType.SERVER, () => t('unit.servers')).with(DeviceType.PRINTER, () => t('unit.printers')).with(DeviceType.OTHER, () => t('unit.others')).exhaustive();
  const {
    mutateTableRows,
    filterError,
    filterWarning
  } = useTableRows();
  const {
    data,
    isPending
  } = useGetExternalItSaasAppIntegrationSyncPreview({
    appIntegrationId,
    deviceType,
    filterError,
    filterWarning
  });
  const {
    data: syncStatus
  } = useGetExternalItSaasAppIntegrationSyncStatus({
    appIntegrationId,
    deviceType
  });
  return <HStack className="h-[calc(100vh-64px)] w-full items-start gap-9 p-10">
      <AddStepperNavigation hash={hash} appIntegrationId={appIntegrationId} syncStatus={syncStatus} isLanscope={isLanscope} />

      <VStack className="h-full min-w-0 flex-1 items-start gap-6 p-0">
        <AddStepperStepTitleCard hash={hash} />

        {isPending && <Processing hash={hash} />}

        {!isPending && <PreviewFile hash={hash} unit={unit} search={search} navigate={navigate} tableColumnDefs={data.tableColumnDefs} tableRows={data} mutateTableRows={mutateTableRows} filterError={filterError} filterWarning={filterWarning} summaryErrorMessage={tPreview('previewFile.summaryErrorMessage')} summaryWarningMessage={tPreview('previewFile.summaryWarningMessage')} />}
      </VStack>
    </HStack>;
}